import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChannelBadge,
  type ChannelKind,
  EmptyState,
  Input,
  Label,
  ListPage,
  Progress,
  Skeleton,
  StatusBadge,
} from '@skylabs-digital/backoffice-kit';
import { Search } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  UserBudgetState,
  UserPreferenceEntry,
  UserRecentNotification,
} from '../api/userState.api';
import { userStateApi } from '../api/userState.api';
import { useSelectedApp } from '../context/SelectedApp';
import { statusTone } from '../utils/status-tone';

interface PreferencesCardProps {
  preferences: UserPreferenceEntry[];
  loading: boolean;
  /** true cuando la consulta fue sin tenant y la API no pudo resolverlas. */
  unresolved: boolean;
}

function PreferencesCard({ preferences, loading, unresolved }: PreferencesCardProps) {
  // Solo interesan las que alguien cambio a mano: la matriz completa son
  // canales x categorias en su default y no dice nada sobre este usuario.
  const overrides = preferences.filter((p) => p.source !== 'default');
  return (
    <Card>
      <CardHeader>
        <CardTitle>Preferences</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : unresolved ? (
          <p className="text-sm text-muted-foreground">
            Sin resolver: las preferencias son por tenant. Indicá un Tenant ID para verlas.
          </p>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sin overrides: este usuario usa los defaults en los {preferences.length} pares de canal
            y categoría.
          </p>
        ) : (
          <div className="space-y-2">
            {overrides.map((p) => (
              <div
                key={`${p.channel}:${p.messageCategory}`}
                className="flex items-center gap-2 text-sm"
              >
                <ChannelBadge channel={p.channel as ChannelKind} />
                <span className="text-xs text-muted-foreground">{p.messageCategory}</span>
                <StatusBadge tone={p.enabled ? 'success' : 'danger'}>
                  {p.enabled ? 'Habilitada' : 'Bloqueada'}
                </StatusBadge>
                <span className="text-xs text-muted-foreground">por {p.source}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OptOutsCard({
  optOuts,
  loading,
}: {
  optOuts: Array<{ channel: string }>;
  loading: boolean;
}) {
  const optedOut = optOuts;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Opt-outs</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={`s-${i}`} className="h-6 w-20" />
            ))}
          </div>
        ) : optedOut.length === 0 ? (
          <p className="text-sm text-muted-foreground">No opt-outs recorded for this user.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {optedOut.map((ch) => (
              <StatusBadge key={ch.channel} tone="danger">
                {ch.channel}
              </StatusBadge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface BudgetUsageCardProps {
  budgets: UserBudgetState[];
  loading: boolean;
}

function BudgetUsageCard({ budgets, loading }: BudgetUsageCardProps) {
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Frequency budget usage</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={`s-${i}`} className="h-10 w-full" />
            ))}
          </div>
        ) : budgets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No budget data.</p>
        ) : (
          <div className="space-y-3">
            {budgets.map((b) => {
              // weeklyLimit es nullable: null = sin tope configurado, que no es
              // lo mismo que un tope de 0 (eso bloquearia todo).
              const pct =
                b.weeklyLimit && b.weeklyLimit > 0
                  ? Math.round((b.currentCount / b.weeklyLimit) * 100)
                  : 0;
              const tone = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success';
              return (
                <div key={b.messageCategory} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <StatusBadge tone="accent" dot={false}>
                      {b.messageCategory}
                    </StatusBadge>
                    <span className="tabular-nums text-muted-foreground">
                      {b.currentCount} / {b.weeklyLimit ?? '∞'}{' '}
                      {b.remaining === null ? (
                        <span className="ml-1">(sin tope)</span>
                      ) : (
                        <span className="ml-1">({b.remaining} left)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={Math.min(pct, 100)} className="flex-1" />
                    <StatusBadge tone={tone} dot={false} className="min-w-[44px] justify-center">
                      {pct}%
                    </StatusBadge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface RecentNotificationsCardProps {
  notifications: UserRecentNotification[];
  loading: boolean;
}

/**
 * Antes esto mostraba un numero grande (`count`), pero la API manda las
 * ultimas 20 notificaciones con su estado y su ultimo error. Para la pregunta
 * que esta pantalla responde ("por que este usuario no recibe") el detalle es
 * justamente lo unico que sirve.
 */
function RecentNotificationsCard({ notifications, loading }: RecentNotificationsCardProps) {
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Recent notifications</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin notificaciones recientes.</p>
        ) : (
          <div className="space-y-2">
            {notifications.map((n) => (
              <div key={n.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Link
                  to={`/notifications/${n.id}`}
                  className="font-mono text-xs text-primary hover:underline"
                >
                  {n.id.slice(0, 8)}…
                </Link>
                <ChannelBadge channel={n.channel as ChannelKind} />
                <StatusBadge tone={statusTone(n.status)}>{n.status}</StatusBadge>
                <span className="text-xs text-muted-foreground">{n.messageCategory}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(n.createdAt).toLocaleString()}
                </span>
                {n.deliveryAttempts > 0 ? (
                  <span className="text-xs text-muted-foreground">
                    {n.deliveryAttempts} intento{n.deliveryAttempts === 1 ? '' : 's'}
                  </span>
                ) : null}
                {n.lastError ? (
                  <span className="text-xs text-destructive" title={n.lastError}>
                    {n.lastError}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UserInspectorPage() {
  const [userId, setUserId] = useState('');
  const [tenantId, setTenantId] = useState('');

  const [inspecting, setInspecting] = useState(false);
  const [query, setQuery] = useState<{
    userId: string;
    tenantId?: string;
    appId: string;
  } | null>(null);

  // El estado de un usuario es por app (preferencias, budgets y opt-outs
  // cuelgan de appId), y desde #148 la sesion de superusuario no aporta una:
  // sin `?appId` el backend responde 403. La app sale del selector global del
  // header (antes esta pantalla tenia el suyo).
  const { appId } = useSelectedApp();

  const handleInspect = () => {
    if (!userId.trim() || !appId) return;
    setQuery({ userId: userId.trim(), tenantId: tenantId.trim() || undefined, appId });
    setInspecting(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleInspect();
  };

  return (
    <ListPage
      title="User Inspector"
      description="Look up per-user notification state, budgets, and recent activity."
    >
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-[2fr_2fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="ui-user">User ID *</Label>
              <Input
                id="ui-user"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter user ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ui-tenant">Tenant ID (optional)</Label>
              <Input
                id="ui-tenant"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter tenant ID"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleInspect} disabled={!userId.trim() || !appId}>
                <Search /> Inspect
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {!inspecting ? (
        <EmptyState
          icon={<Search className="h-5 w-5" />}
          title="Enter a User ID to inspect"
          description="Look up preferences, opt-outs, budget usage, and recent notifications for any user."
        />
      ) : query ? (
        <UserInspectorResults userId={query.userId} tenantId={query.tenantId} appId={query.appId} />
      ) : null}
    </ListPage>
  );
}

function UserInspectorResults({
  userId,
  tenantId,
  appId,
}: {
  userId: string;
  tenantId?: string;
  appId: string;
}) {
  const { data, loading, error, refetch } = userStateApi
    .withParams({ userId })
    .withQuery(tenantId ? { appId, tenantId } : { appId })
    .useRecord();

  if (error) {
    return (
      <EmptyState
        title={`Failed to load state for user "${userId}"`}
        description={error instanceof Error ? error.message : 'Try again in a moment.'}
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* El inspector es de lectura: cuando el operador quiere CAMBIAR algo,
        este link lo lleva a Preferences con la app, el tenant y el usuario ya
        puestos, en vez de hacerlo elegir todo de nuevo. */}
      <div className="md:col-span-2 flex justify-end">
        <Link
          to={`/preferences?appId=${encodeURIComponent(appId)}&userId=${encodeURIComponent(userId)}${
            tenantId ? `&tenantId=${encodeURIComponent(tenantId)}` : ''
          }`}
          className="text-sm text-primary hover:underline"
        >
          Editar preferencias y opt-outs de este usuario →
        </Link>
      </div>
      <PreferencesCard
        preferences={data?.preferences ?? []}
        loading={loading}
        unresolved={(data?.unresolvedWithoutTenant ?? []).includes('preferences')}
      />
      <OptOutsCard optOuts={data?.optOuts ?? []} loading={loading} />
      <BudgetUsageCard budgets={data?.budgets ?? []} loading={loading} />
      <RecentNotificationsCard notifications={data?.recentNotifications ?? []} loading={loading} />
    </div>
  );
}
